import Icon from 'gcs-common/components/Icon/Icon';
import { ICON, ICON_COLOR } from 'gcs-common/constants/IconConstants';
import { useSidebar } from 'gcs-common/design-system/ui/sidebar';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { CHAT_ROUTES } from '../../../constants/routes';
import { TOOL_TABS } from '../../../constants/toolUIFlags';
import FEATURE_FLAGS from '../../../helper/featureFlagsHelper';
import { getSelectedChannelId } from '../../../slices/channels/channelsSelectors';
import { getHasFeatureFlag, getIsAgent } from '../../../slices/currentUser/currentUserSelectors';
import { getIsMobile } from '../../../slices/init/initSelectors';
import {
  type ORDER_STATUS,
  ORDER_STATUS_COLOR,
  ORDER_STATUS_DESCRIPTION_AGENT,
  ORDER_STATUS_DESCRIPTION_CUSTOMER,
} from '../../../slices/quickOrdering/quickOrderingConstants';
import { selectOrder } from '../../../slices/quickOrdering/quickOrderingSlice';
import { setToolbarTab } from '../../../slices/uiState/uiStateSlice';
import styles from './styles.module.scss';

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function OrderMessage({ order }: { order: any }) {
  const isMobile = useSelector(getIsMobile);
  return isMobile ? <OrderMessageMobile order={order} /> : <OrderMessageWeb order={order} />;
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function OrderMessageWeb({ order }: { order: any }) {
  const dispatch = useDispatch();
  const sidebar = useSidebar();

  function onSelectOrder() {
    dispatch(selectOrder({ orderId: order.id }));
    sidebar.setOpen(true);
    dispatch(setToolbarTab({ tab: TOOL_TABS.ORDERS }));
  }

  return <OrderMessageCommon order={order} onSelectOrder={onSelectOrder} />;
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function OrderMessageMobile({ order }: { order: any }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedChannelId: string = useSelector(getSelectedChannelId);

  function onSelectOrder() {
    dispatch(selectOrder({ orderId: order.id }));
    navigate(CHAT_ROUTES.QUICK_ORDER(selectedChannelId));
  }

  return <OrderMessageCommon order={order} onSelectOrder={onSelectOrder} />;
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const OrderMessageCommon = ({ order, onSelectOrder }: { order: any; onSelectOrder: () => void }) => {
  const status = order?.status as ORDER_STATUS;
  const isAgent = useSelector(getIsAgent) as boolean;
  const hasFeatureFlag = useSelector(getHasFeatureFlag(FEATURE_FLAGS.QUICKORDERING_ENABLED));
  if (!hasFeatureFlag) {
    return 'Diese Nachricht kann mit Deiner Version von BEEM noch nicht angezeigt werden';
  }
  return (
    <div className={styles.process} role="presentation" onClick={onSelectOrder}>
      <div className={styles.magicCubeWrapper}>
        <Icon Icon={ICON.QUICKORDERING} color={ICON_COLOR.PRIMARY_BUTTON} />
      </div>
      <div>
        <div className={styles.processWrapper}>
          <div className={styles.processHeader}>{order.title || 'Bestellung'}</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: '0 0 auto',
            }}
          >
            <div
              style={{
                backgroundColor: ORDER_STATUS_COLOR[status],
                height: '10px',
                width: '10px',
                borderRadius: '50%',
                marginRight: '5px',
                marginBottom: '2px',
                flex: '0 0 auto',
              }}
            />
            <div
              style={{
                fontSize: '12px',
                color: ORDER_STATUS_COLOR[status],
              }}
            >
              {isAgent ? ORDER_STATUS_DESCRIPTION_AGENT[status] : ORDER_STATUS_DESCRIPTION_CUSTOMER[status]}
            </div>
          </div>
          {order.customerOrderText && <div className={styles.processDetail}>{order.customerOrderText}</div>}
          {order.customerOrderNumber && <div className={styles.processDetail}>{order.customerOrderNumber}</div>}
          <div className={styles.processDetail}>
            Positionen:
            {order.orderPositions?.length}
          </div>
        </div>
      </div>
    </div>
  );
};

OrderMessage.propTypes = {
  order: PropTypes.any,
};

export default OrderMessage;
